import { Glyph } from '../chrome/Glyph.js';
import type { Facet, FacetState } from '../model/filter.js';

/**
 * Search box and the facet grid (design 181–208).
 *
 * ## The copy rules bite hardest here
 *
 * No caption explains what the facets do. A button that is armed looks armed and
 * a button that excludes looks like it excludes; a legend saying "click twice to
 * exclude" is the console explaining its own mechanism, which
 * `.claude/CLAUDE.md` forbids outright. The tri-state is discoverable in one
 * click and the third click puts it back.
 *
 * The only number in the block is the count of armed values beside FILTER — the
 * operator's own clicks. Never "showing 5 of 12": the copy rules forbid the
 * phrasing and must-not #1 forbids any count of what the viewer was not given.
 *
 * The empty state is `NO MATCH` and nothing else. Not "try a different search",
 * not "0 results for …" — the search box is right there with the words still in
 * it.
 */

export interface FacetRowProps {
  readonly search: string;
  readonly onSearch: (value: string) => void;
  readonly filterOpen: boolean;
  readonly onToggleFilter: () => void;
  readonly facets: readonly Facet[];
  readonly armed: number;
  readonly onCycle: (facet: string, value: string) => void;
  readonly onClear: () => void;
  readonly searchRef?: React.RefObject<HTMLInputElement>;
}

export function FacetRow(props: FacetRowProps): JSX.Element {
  return (
    <div
      data-testid="rail-filter"
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '8px 12px',
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ position: 'relative', width: '100%' }}>
        <span
          style={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-disabled)',
            pointerEvents: 'none',
          }}
        >
          <Glyph name="search" size={15} />
        </span>
        <input
          id="bs-search"
          data-testid="rail-search"
          type="text"
          value={props.search}
          ref={props.searchRef}
          onChange={(event) => props.onSearch(event.target.value)}
          placeholder="Search id, type or name  ·  /"
          style={{
            width: '100%',
            height: 28,
            padding: '0 8px 0 28px',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            color: 'var(--text)',
            fontFamily: 'var(--font-prose)',
            fontSize: 13,
            boxSizing: 'border-box',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 20 }}>
        <button
          type="button"
          data-testid="rail-filter-toggle"
          aria-expanded={props.filterOpen}
          onClick={props.onToggleFilter}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '0 2px',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              color: 'var(--text-muted)',
            }}
          >
            FILTER
          </span>
          {props.armed > 0 && (
            <span
              data-testid="rail-filter-count"
              style={{
                fontFamily: 'var(--font-data)',
                fontSize: 11,
                fontWeight: 600,
                padding: '1px 5px',
                background: 'var(--primary-tint)',
                color: 'var(--primary)',
              }}
            >
              {props.armed}
            </span>
          )}
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              color: 'var(--text-3)',
              transform: props.filterOpen ? 'rotate(90deg)' : 'none',
            }}
          >
            ›
          </span>
        </button>
        {props.armed > 0 && (
          <button
            type="button"
            data-testid="rail-filter-clear"
            onClick={props.onClear}
            style={{
              display: 'flex',
              alignItems: 'center',
              height: 20,
              padding: '0 8px',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              color: 'var(--text-3)',
              fontFamily: 'var(--font-ui)',
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '.14em',
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            CLEAR
          </button>
        )}
        <span style={{ flex: 1 }} />
      </div>

      {props.filterOpen && (
        <div
          data-testid="rail-facets"
          style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '4px 0' }}
        >
          {props.facets.map((facet) => (
            <div key={facet.id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{
                  fontFamily: 'var(--font-ui)',
                  fontSize: 12,
                  fontWeight: 600,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                }}
              >
                {facet.label}
              </span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {facet.values.map((value) => (
                  <button
                    key={value.value}
                    type="button"
                    data-testid={'facet-' + facet.id + '-' + value.value}
                    data-facet-state={value.state}
                    aria-pressed={value.state !== 'OFF'}
                    onClick={() => props.onCycle(facet.id, value.value)}
                    style={{
                      height: 24,
                      padding: '0 8px',
                      background: FACET_TONE[value.state].bg,
                      border: '1px solid ' + FACET_TONE[value.state].border,
                      borderRadius: 'var(--radius)',
                      color: FACET_TONE[value.state].color,
                      fontFamily: 'var(--font-data)',
                      fontSize: 11,
                      letterSpacing: '.04em',
                      textDecoration: value.state === 'EXCLUDE' ? 'line-through' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    {value.value}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Three states, three treatments, no legend.
 *
 * EXCLUDE strikes the label through as well as recolouring it — the two armed
 * states have to be distinguishable without colour, and "this value is struck
 * out" is the one convention that needs no explaining.
 */
const FACET_TONE: Readonly<Record<FacetState, { bg: string; border: string; color: string }>> =
  Object.freeze({
    OFF: { bg: 'transparent', border: 'var(--border)', color: 'var(--text-3)' },
    INCLUDE: { bg: 'var(--primary-tint)', border: 'var(--primary)', color: 'var(--primary)' },
    EXCLUDE: { bg: 'var(--red-surface)', border: 'var(--red)', color: 'var(--red-text)' },
  });
